import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAuth } from '../../AuthContext';
import { onOpenPlanBillingRequest } from '../../utils/company/officeLockDialogBus';
import { pushToast } from '../common/toast/toastStore';
import { openPlanBilling } from '../../utils/company/openPlanBilling';

/**
 * KAN-204 — the app-level listener for "Open Plan & billing" (the lock dialog's
 * button, the in-form notice's action). Mounted once per guarded route inside
 * the Router (ProtectedRoute), so it exists on the admin home AND on full pages
 * such as /log-job. Owner + admins only (role 'admin'): any other role asking
 * does nothing — the copy never offers the action to them, and the server is the
 * boundary either way. See `openPlanBilling` for what a request does.
 */
export default function OpenPlanBillingHandler() {
  const { role, sessionUser, tenantId } = useAuth();
  const intl = useIntl();
  const intlRef = useRef(intl);
  intlRef.current = intl;
  const sessionRef = useRef({});
  sessionRef.current = { userId: sessionUser?.id || null, tenantId: tenantId || null };
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const isAdmin = role === 'admin';
  useEffect(() => {
    if (!isAdmin) return undefined;
    return onOpenPlanBillingRequest(() => {
      void openPlanBilling({
        navigate: (...args) => navigateRef.current(...args),
        getSession: () => sessionRef.current,
      }).then((outcome) => {
        // Stay / declined is the user's own choice (silent); a busy or stuck overlay must never fail silently.
        if (outcome === 'not-closed') {
          pushToast({
            type: 'info',
            message: intlRef.current.formatMessage({ id: 'companyAccess.openPlan.finishFirst', defaultMessage: "Finish what's open first, then try again." }),
          });
        }
      });
    });
  }, [isAdmin]);
  return null;
}
